import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { AlertCircleIcon, ArrowRightIcon, ChevronRightIcon, RefreshCwIcon } from "lucide-react"
import { Link } from "react-router"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/shadcn/alert"
import { Button } from "@/components/ui/shadcn/button"
import { Skeleton } from "@/components/ui/shadcn/skeleton"
import type { Category } from "@/types/product"
import { cn } from "@/lib/utils"

import {
  getCategoryHref,
  hasCategoryChildren,
  hasMeaningfulDescription,
  normalizeCategoryTree,
  splitCatalogDisplayColumns,
  splitRootCategories,
  type CatalogDisplayNode,
} from "./catalogMenuModel"

type CatalogMenuContentProps = {
  categories: Category[]
  activeCategory: Category | null
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  onActivateCategory: (category: Category) => void
  onNavigate: () => void
}

const ROOT_GROUPS = [
  { key: "mainRoots", title: "Основные разделы" },
  { key: "miscRoots", title: "Прочее" },
] as const

export const CatalogMenuContent = ({
  categories,
  activeCategory,
  isLoading,
  isError,
  onRetry,
  onActivateCategory,
  onNavigate,
}: CatalogMenuContentProps) => {
  if (isLoading) {
    return <CatalogMenuSkeleton />
  }

  if (isError) {
    return (
      <CatalogState
        title="Каталог пока недоступен"
        description="Не удалось загрузить категории. Попробуйте обновить список."
        action="Попробовать снова"
        onAction={onRetry}
        destructive
      />
    )
  }

  if (categories.length === 0) {
    return (
      <CatalogState
        title="Каталог пока недоступен"
        description="Категории не пришли из текущего API."
        action="Обновить"
        onAction={onRetry}
      />
    )
  }

  const groups = splitRootCategories(categories)
  const selectedCategory = activeCategory ?? categories[0]

  return (
    <div className="grid h-full grid-cols-[minmax(0,292px)_minmax(0,1fr)]">
      <aside className="min-h-0 border-r border-[var(--scaena-control-border)] bg-white/48">
        <ScrollFadeFrame className="h-full" scrollClassName="flex flex-col gap-5 p-4 pr-6">
          {ROOT_GROUPS.map(({ key, title }) => {
            const items = groups[key]

            if (items.length === 0) {
              return null
            }

            return (
              <section key={key} className="flex flex-col gap-2">
                <h3 className="px-3 text-xs font-[550] uppercase tracking-[0.08em] text-muted-foreground">
                  {title}
                </h3>
                <div className="flex flex-col gap-1">
                  {items.map((category) => (
                    <RootCategoryLink
                      key={category.ref_key}
                      category={category}
                      active={selectedCategory?.ref_key === category.ref_key}
                      emphasized={key === "mainRoots"}
                      onActivate={() => onActivateCategory(category)}
                      onNavigate={onNavigate}
                    />
                  ))}
                </div>
              </section>
            )
          })}
        </ScrollFadeFrame>
      </aside>

      <section className="min-h-0 bg-white/18">
        <ActiveCategoryPanel category={selectedCategory} onNavigate={onNavigate} />
      </section>
    </div>
  )
}

const RootCategoryLink = ({
  category,
  active,
  emphasized,
  onActivate,
  onNavigate,
}: {
  category: Category
  active: boolean
  emphasized: boolean
  onActivate: () => void
  onNavigate: () => void
}) => (
  <Link
    to={getCategoryHref(category)}
    className={cn(
      "group flex items-center gap-3 rounded-[var(--scaena-radius-inner)] px-3 py-2.5 text-sm font-[400] leading-snug text-foreground outline-none transition-colors hover:bg-primary/8 hover:text-primary focus-visible:ring-3 focus-visible:ring-ring/15",
      emphasized && "font-[550]",
      active && "bg-primary/10 text-primary"
    )}
    onMouseEnter={onActivate}
    onFocus={onActivate}
    onClick={onNavigate}
    data-active={active ? "" : undefined}
  >
    <span className="min-w-0 flex-1 text-pretty">{category.name}</span>
    {hasCategoryChildren(category) ? (
      <ChevronRightIcon className="size-4 shrink-0 text-primary/55 transition-transform group-hover:translate-x-0.5" />
    ) : null}
  </Link>
)

const ActiveCategoryPanel = ({
  category,
  onNavigate,
}: {
  category: Category
  onNavigate: () => void
}) => {
  const node = normalizeCategoryTree(category)
  const categoryColumns = splitCatalogDisplayColumns(node.children)

  return (
    <ScrollFadeFrame className="h-full" scrollClassName="flex flex-col gap-6 p-6 pr-8">
      <header className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-5">
          <div className="min-w-0">
            <h2 className="text-lg font-[650] leading-tight text-foreground text-pretty">
              {category.name}
            </h2>
            {hasMeaningfulDescription(category) ? (
              <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
                {category.description}
              </p>
            ) : null}
          </div>
          <Button render={<Link to={node.href} onClick={onNavigate} />} size="sm">
            Все товары раздела
            <ArrowRightIcon data-icon="inline-end" />
          </Button>
        </div>
      </header>

      {node.children.length > 0 ? (
        <div className="flex items-start gap-8">
          {categoryColumns.map((column, index) => (
            <div key={index} className="flex min-w-0 flex-1 flex-col gap-4">
              {column.map((child) => (
                <CategoryNodeGroup key={child.category.ref_key} node={child} onNavigate={onNavigate} />
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div>
          <Button render={<Link to={node.href} onClick={onNavigate} />} variant="outline">
            Перейти в категорию
            <ArrowRightIcon data-icon="inline-end" />
          </Button>
        </div>
      )}
    </ScrollFadeFrame>
  )
}

const ScrollFadeFrame = ({
  children,
  className,
  scrollClassName,
}: {
  children: ReactNode
  className?: string
  scrollClassName?: string
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [fadeEdges, setFadeEdges] = useState({ top: false, bottom: false })

  const updateFadeEdges = useCallback(() => {
    const element = scrollRef.current

    if (!element) {
      return
    }

    const nextFadeEdges = {
      top: element.scrollTop > 1,
      bottom: element.scrollTop + element.clientHeight < element.scrollHeight - 1,
    }

    setFadeEdges((currentFadeEdges) => {
      if (
        currentFadeEdges.top === nextFadeEdges.top &&
        currentFadeEdges.bottom === nextFadeEdges.bottom
      ) {
        return currentFadeEdges
      }

      return nextFadeEdges
    })
  }, [])

  useLayoutEffect(() => {
    updateFadeEdges()
  }, [children, updateFadeEdges])

  useEffect(() => {
    const element = scrollRef.current

    if (!element) {
      return
    }

    updateFadeEdges()

    const resizeObserver = new ResizeObserver(updateFadeEdges)
    resizeObserver.observe(element)

    if (element.firstElementChild) {
      resizeObserver.observe(element.firstElementChild)
    }

    return () => resizeObserver.disconnect()
  }, [updateFadeEdges])

  return (
    <div className={cn("relative min-h-0 overflow-hidden", className)}>
      <div
        ref={scrollRef}
        className={cn("scrollbar-hidden h-full overflow-y-auto", scrollClassName)}
        onScroll={updateFadeEdges}
      >
        {children}
      </div>
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-primary/10 via-primary/5 to-transparent opacity-0 transition-opacity duration-150",
          fadeEdges.top && "opacity-100"
        )}
      />
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-primary/10 via-primary/5 to-transparent opacity-0 transition-opacity duration-150",
          fadeEdges.bottom && "opacity-100"
        )}
      />
    </div>
  )
}

const CategoryNodeGroup = ({
  node,
  onNavigate,
}: {
  node: CatalogDisplayNode
  onNavigate: () => void
}) => {
  if (node.children.length === 0) {
    return (
      <Link
        to={node.href}
        className="rounded-[var(--scaena-radius-compact)] px-1 py-0.5 text-sm leading-snug text-foreground outline-none transition-colors hover:text-primary focus-visible:ring-3 focus-visible:ring-ring/15"
        onClick={onNavigate}
      >
        {node.category.name}
      </Link>
    )
  }

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Link
        to={node.href}
        className="rounded-[var(--scaena-radius-compact)] px-1 py-0.5 text-sm font-[550] leading-snug text-foreground outline-none transition-colors hover:text-primary focus-visible:ring-3 focus-visible:ring-ring/15"
        onClick={onNavigate}
      >
        {node.category.name}
      </Link>
      <div className="flex flex-col gap-1">
        {node.children.map((child) => (
          <CategoryChildLink key={child.category.ref_key} node={child} onNavigate={onNavigate} />
        ))}
      </div>
    </div>
  )
}

const CategoryChildLink = ({
  node,
  onNavigate,
}: {
  node: CatalogDisplayNode
  onNavigate: () => void
}) => (
  <div className="flex min-w-0 flex-col gap-1">
    <Link
      to={node.href}
      className="rounded-[var(--scaena-radius-compact)] px-1 py-0.5 text-sm leading-snug text-muted-foreground outline-none transition-colors hover:text-primary focus-visible:ring-3 focus-visible:ring-ring/15"
      onClick={onNavigate}
    >
      {node.category.name}
    </Link>
    {node.children.length > 0 ? (
      <div className="ml-3 flex flex-col gap-1 border-l border-[var(--scaena-control-border)] pl-3">
        {node.children.map((child) => (
          <CategoryChildLink key={child.category.ref_key} node={child} onNavigate={onNavigate} />
        ))}
      </div>
    ) : null}
  </div>
)

const CatalogMenuSkeleton = () => (
  <div className="grid h-full grid-cols-[minmax(0,292px)_minmax(0,1fr)]">
    <div className="border-r border-[var(--scaena-control-border)] bg-white/48 p-4">
      <div className="flex flex-col gap-3">
        <Skeleton className="h-4 w-32" />
        {Array.from({ length: 10 }).map((_, index) => (
          <Skeleton key={index} className="h-10 w-full rounded-[var(--scaena-radius-inner)]" />
        ))}
      </div>
    </div>
    <div className="p-6">
      <div className="flex flex-col gap-5">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-4 w-full max-w-lg" />
        <div className="grid grid-cols-2 gap-5 pt-2">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="flex flex-col gap-2">
              <Skeleton className="h-5 w-44" />
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-4 w-28" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
)

const CatalogState = ({
  title,
  description,
  action,
  onAction,
  destructive = false,
}: {
  title: string
  description: string
  action: string
  onAction: () => void
  destructive?: boolean
}) => (
  <div className="h-full p-6">
    <Alert variant={destructive ? "destructive" : "default"}>
      <AlertCircleIcon />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>{description}</AlertDescription>
    </Alert>
    <Button className="mt-5" variant="outline" onClick={onAction}>
      <RefreshCwIcon data-icon="inline-start" />
      {action}
    </Button>
  </div>
)
